import { PickingInfo } from "deck.gl";
import { feature, point } from "@turf/helpers";
import { getGeom } from "@turf/turf";
import buffer from "@turf/buffer";
import { SelectionType, SelectionFeature } from "src/js/stores/map/store";
import { PlanningApplicationFeature } from "react-migration/domains/planning/types";
import { PlanningApplicationClusteredViewportSearchMapLayer } from "./PlanningApplicationsMapLayer/PlanningApplicationClusteredViewportSearchMapLayer";
import { fetchPlanningApplicationById } from "./usePlanningAppsById";

const POINT_BUFFER_RADIUS_METERS = 2;

function clickOriginatesFromPlanningLayer(info: PickingInfo<unknown>): info is PickingInfo<PlanningApplicationFeature> {
  return info.layer instanceof PlanningApplicationClusteredViewportSearchMapLayer;
}

type PlanningClickResolver = (pickInfo: PickingInfo<unknown>) => Promise<SelectionFeature | null>;

export const planningApplicationsClickResolver: PlanningClickResolver = async (pickInfo) => {
  const planningFeature = pickInfo.object as PlanningApplicationFeature | null;

  if (!clickOriginatesFromPlanningLayer(pickInfo)) {
    return null;
  }

  if (!planningFeature) {
    return null;
  }

  const { layerName } = planningFeature.properties;

  try {
    switch (layerName) {
      case "dots":
        return makeDotSelection(planningFeature);
      case "boundaries":
        return makeBoundarySelection(planningFeature);
    }
  } catch (e) {
    console.error(`Failed to select planning application "${planningFeature.properties.id}".`, e);
    return null;
  }
};

export function makeDotSelection({ geometry, properties }: PlanningApplicationFeature): SelectionFeature {
  const { id } = properties;
  const areaAroundPount = getGeom(buffer(geometry, POINT_BUFFER_RADIUS_METERS, { units: "meters" }));

  const renderGeometry = point(geometry.coordinates as [number, number], {
    // @ts-expect-error this radius is added by the PlanningApplicationLayer
    radius: properties.__radius,
  });

  return {
    type: SelectionType.PLANNING_APPLICATION,
    id,
    feature: feature(areaAroundPount),
    savable: true,
    savableGeometry: renderGeometry,
    __renderGeometry: renderGeometry,
  };
}

export async function makeBoundarySelection({
  properties: { id },
}: PlanningApplicationFeature): Promise<SelectionFeature> {
  const { boundary } = await fetchPlanningApplicationById(id);

  if (!boundary) {
    throw new Error("Planning application boundary not found for feature on boundary layer.");
  }

  return {
    type: SelectionType.PLANNING_APPLICATION,
    id,
    feature: feature(boundary),
  };
}
